// apps/web/src/features/call-center/components/CallCenterLeadsTable.tsx
//
// Read-only own-leads table for the external call-center agent (#1398).
// Mirrors the china-rates request-workspace shell: a sticky `WorkspaceTable`
// on desktop, mobile cards below the narrow breakpoint, a china-rates empty
// state, and a cursor "Load more" button.
import { useTranslation } from 'react-i18next';
import type { CallCenterMyLeadsItem } from '@magimp/shared';
import { Button, Icon, WorkspaceTable, type WorkspaceTableColumn } from '../../../ui/index.js';
import { GRID_TEMPLATE } from './callCenterLeadGrid.js';
import { CallCenterLeadRow } from './CallCenterLeadRow.js';
import { CallCenterLeadCard } from './CallCenterLeadCard.js';

export interface CallCenterLeadsTableProps {
  leads: readonly CallCenterMyLeadsItem[];
  narrow: boolean;
  loading: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onOpenLead: (lead: CallCenterMyLeadsItem) => void;
  onLoadMore: () => void;
}

function EmptyState({ loading, narrow }: { loading: boolean; narrow: boolean }) {
  const { t } = useTranslation('callCenter');
  return (
    <div
      data-testid={loading ? 'cc-leads-loading' : 'cc-leads-empty'}
      style={{
        gridColumn: narrow ? undefined : '1 / -1',
        padding: '64px 20px',
        textAlign: 'center',
        color: 'var(--fg-3)',
      }}
    >
      <Icon name="inbox" size={28} style={{ color: 'var(--fg-4)' }} />
      <div style={{ marginTop: 10, fontSize: 13 }}>
        {loading ? t('selfService.loading') : t('selfService.empty')}
      </div>
    </div>
  );
}

export function CallCenterLeadsTable({
  leads,
  narrow,
  loading,
  hasNextPage,
  isFetchingNextPage,
  onOpenLead,
  onLoadMore,
}: CallCenterLeadsTableProps) {
  const { t } = useTranslation('callCenter');
  const columns: readonly WorkspaceTableColumn[] = [
    { key: 'phone', label: t('selfService.columns.phone') },
    { key: 'name', label: t('selfService.columns.name') },
    { key: 'comment', label: t('selfService.columns.comment') },
    { key: 'audioUrl', label: t('selfService.columns.audioUrl') },
    { key: 'createdAt', label: t('selfService.columns.createdAt') },
  ];

  return (
    <div style={{ flex: 1, overflow: 'auto', minHeight: 0 }} data-testid="cc-leads-table">
      {!narrow && (
        <WorkspaceTable columnsTemplate={GRID_TEMPLATE} columns={columns}>
          {leads.map((lead) => (
            <CallCenterLeadRow key={lead.id} lead={lead} onActivate={() => onOpenLead(lead)} />
          ))}
          {leads.length === 0 && <EmptyState loading={loading} narrow={false} />}
        </WorkspaceTable>
      )}
      {narrow && (
        <ul
          style={{
            listStyle: 'none',
            margin: 0,
            padding: '12px 12px 0',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          {leads.map((lead) => (
            <li key={lead.id} style={{ listStyle: 'none' }}>
              <CallCenterLeadCard lead={lead} onActivate={() => onOpenLead(lead)} />
            </li>
          ))}
          {leads.length === 0 && (
            <li style={{ listStyle: 'none' }}>
              <EmptyState loading={loading} narrow />
            </li>
          )}
        </ul>
      )}
      {hasNextPage && (
        <div style={{ padding: '14px 24px 22px', display: 'flex', justifyContent: 'center' }}>
          <Button
            kind="secondary"
            size="sm"
            data-testid="cc-leads-load-more"
            disabled={isFetchingNextPage}
            onClick={onLoadMore}
          >
            {isFetchingNextPage ? t('selfService.loading') : t('selfService.loadMore')}
          </Button>
        </div>
      )}
    </div>
  );
}
